import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useParams, useSearchParams } from 'react-router';

import { roles } from 'src/utils/roles';

import { CONFIG } from 'src/config-global';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';
import { PageTransition } from 'src/components/animate/page-transition';

import { ReportsRepositoryLayout } from 'src/sections/reporting/list';

import { AuthGuard, RoleBasedGuard, FeatureFlagGuard } from 'src/auth/guard';

import { paths } from '../paths';

// ----------------------------------------------------------------------

// Pages
const ReportingViewPage = lazy(() => import('src/pages/reporting/render/view'));
const ReportsListPage = lazy(() => import('src/pages/reporting/list'));
const InStoreReportsListPage = lazy(() => import('src/pages/reporting/in-store-reports-list'));
const ViewInStoreReportPage = lazy(() => import('src/pages/reporting/in-store-reports-view'));
const InStoreReportScheduleFormPage = lazy(() => import('src/pages/reporting/in-store-reports-schedule-form'));
const InStoreReportsScheduleListPage = lazy(() => import('src/pages/reporting/in-store-reports-schedule-list'));
const SchedulesListPage = lazy(() => import('src/pages/reporting/schedules'));
const ReportBuilderViewPage = lazy(() => import('src/pages/reporting/report-builder-view'));
const ReportBuilderListPage = lazy(() => import('src/pages/reporting/report-builder-list'));
const EnvysionViewPage = lazy(() => import('src/pages/envysion/view'));

// ----------------------------------------------------------------------

/** Deprecated `/favorites/*` → `/reports/*` for old bookmarks. */
function FavoritesToReportsRedirect() {
  const { '*': splat } = useParams();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  const to = `${paths.reports.view(`/${splat || ''}`)}${search ? `?${search}` : ''}`;

  return <Navigate to={to} replace />;
}

// The tenant must have oneview.config.report-builder-enabled, and the user ROLE_ADHOC (or admin).
const reportBuilderGuard = (page) => (
  <FeatureFlagGuard feature="reportBuilder">
    <RoleBasedGuard roles={[roles.ADHOC]}>{page}</RoleBasedGuard>
  </FeatureFlagGuard>
);

// The envysion* features are per user: the tenant's oneview.config.envysion-* flag plus
// ROLE_ENVYSION (and ROLE_TB/ROLE_PH for the ticket views), or ROLE_ADMINISTRATOR.
const envysionPage = (feature, view, title) => (
  <FeatureFlagGuard feature={feature}>
    <EnvysionViewPage view={view} title={title} />
  </FeatureFlagGuard>
);

const layoutContent = (
  <DashboardLayout>
    <Suspense fallback={<LoadingScreen />}>
      <PageTransition>
        <Outlet />
      </PageTransition>
    </Suspense>
  </DashboardLayout>
);

export const reportingRoutes = [
  {
    path: paths.root,
    element: CONFIG.auth.skip ? <>{layoutContent}</> : <AuthGuard>{layoutContent}</AuthGuard>,
    children: [
      {
        roles: [roles.VIEW_REPORTS],
        element: (
          <RoleBasedGuard roles={[roles.VIEW_REPORTS]}>
            <ReportsRepositoryLayout />
          </RoleBasedGuard>
        ),
        children: [
          { path: 'reports/list', element: <ReportsListPage /> },
          { path: 'reports/*', element: <ReportingViewPage /> },
          { path: 'favorites/*', element: <FavoritesToReportsRedirect /> },
          { path: 'dashboards/*', element: <ReportingViewPage /> },
        ],
      },
      {
        // Not under ROLE_VIEW_REPORTS: the Envysion views have their own access rules
        element: <ReportsRepositoryLayout />,
        children: [
          { path: paths.envysion.cameraView, element: envysionPage('envysionCameraView', 'cameraView', 'Envysion Report') },
          { path: paths.envysion.ticketViewPh, element: envysionPage('envysionTicketViewPh', 'ticketViewPh', 'Envysion PH Ticket View') },
          { path: paths.envysion.ticketViewTb, element: envysionPage('envysionTicketViewTb', 'ticketViewTb', 'Envysion TB Ticket View') },
        ],
      },
      {
        roles: [roles.VIEW_REPORTS],
        children: [
          { path: 'schedules', element: <RoleBasedGuard roles={[roles.VIEW_REPORTS]} children={<SchedulesListPage />} /> },
          { path: paths.reports.schedules, element: <RoleBasedGuard roles={[roles.VIEW_REPORTS]} children={<SchedulesListPage />} /> },
        ],
      },
      {
        roles: [roles.ADHOC],
        path: paths.reportBuilder.root,
        children: [
          { path: 'list', element: reportBuilderGuard(<ReportBuilderListPage />), index: true },
          { path: 'view', element: reportBuilderGuard(<ReportBuilderViewPage />) },
        ],
      },
      {
        roles: [roles.VIEW_REPORTS],
        path: paths.inStoreReports.root,
        children: [
          { path: 'list', element: <RoleBasedGuard roles={[roles.VIEW_REPORTS]} children={<InStoreReportsListPage />} />, index: true },
          { path: 'view', element: <RoleBasedGuard roles={[roles.VIEW_REPORTS]} children={<ViewInStoreReportPage />} /> },
        ]
      },
      {
        roles: [roles.VIEW_REPORTS],
        path: paths.inStoreReports.schedules.root,
        children: [
          { path: 'list', element: <RoleBasedGuard roles={[roles.VIEW_REPORTS]} children={<InStoreReportsScheduleListPage />} /> },
          { path: 'new', element: <RoleBasedGuard roles={[roles.VIEW_REPORTS]} children={<InStoreReportScheduleFormPage />} /> },
          { path: ':id/edit', element: <RoleBasedGuard roles={[roles.VIEW_REPORTS]} children={<InStoreReportScheduleFormPage />} /> },
        ],
      }
    ],
  },
];
